import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useVitalSignsKey } from '../hooks';
import { T, useT, useLang } from '../Translation/index';
import TableNameLink from './TableNameLink';
import { phone } from '../style/media';

// === Design tokens ===
const colors = {
  background: '#f8f9fa',
  card: '#ffffff',
  border: '#e1e3e4',
  primary: '#1976d2',
  secondary: '#006e1c',
  tertiary: '#884f00',
  error: '#ba1a1a',
  text: '#191c1d',
  textMuted: '#6c757d',
  hoverRow: '#f0f7ff',
};

// === Styled Components ===

const Wrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background-color: ${colors.background};
  ${phone`
    padding: 12px 0;
  `}
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${phone`
    gap: 12px;
    padding: 0;
    max-width: 100%;
  `}
`;

const Card = styled.section`
  background: ${colors.card};
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid ${colors.border};
  overflow: hidden;
  ${phone`
    border-radius: 0;
    border-left: none;
    border-right: none;
    box-shadow: none;
  `}
`;

const CardBody = styled.div`
  padding: 24px;
  ${phone`
    padding: 20px 16px;
  `}
`;

const CardHeader = styled.div`
  padding: 16px 24px;
  background: #fafbfc;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #eeeeee;
  ${phone`
    padding: 14px 16px;
  `}
`;

const CardHeaderIcon = styled.span`
  font-size: 20px;
`;

const CardHeaderTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
`;

// --- Wellness Hero ---
const WellnessCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const WellnessLabel = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 0 16px 0;
`;

const RingContainer = styled.div`
  position: relative;
  width: 220px;
  height: 220px;
  margin-bottom: 24px;
  ${phone`
    width: 180px;
    height: 180px;
    margin-bottom: 16px;
  `}
`;

const RingSvg = styled.svg`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const RingCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const RingScore = styled.span<{ level: string }>`
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ level }) => {
    if (level === 'HIGH') return '#388e3c';
    if (level === 'LOW') return '#d32f2f';
    return '#f57c00';
  }};
  ${phone`
    font-size: 36px;
  `}
`;

const RingBadge = styled.span<{ level: string }>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${({ level }) => {
    if (level === 'HIGH') return '#e8f5e9';
    if (level === 'LOW') return '#ffebee';
    return '#fff3e0';
  }};
  color: ${({ level }) => {
    if (level === 'HIGH') return '#2e7d32';
    if (level === 'LOW') return '#c62828';
    return '#e65100';
  }};
`;

const SegmentedBar = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const SegmentsWrapper = styled.div`
  display: flex;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const SegmentLow = styled.div`
  flex: 0 0 40%;
  background: ${colors.error};
  opacity: 0.4;
`;

const SegmentMid = styled.div`
  flex: 0 0 30%;
  background: ${colors.tertiary};
  opacity: 0.4;
`;

const SegmentHigh = styled.div`
  flex: 0 0 30%;
  background: ${colors.secondary};
`;

const SegmentLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${colors.textMuted};
`;

// --- Metric Cards Bento ---
const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
  ${phone`
    grid-template-columns: 1fr;
    gap: 12px;
  `}
`;

const MetricCard = styled.div`
  background: ${colors.card};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid ${colors.border};
  padding: 20px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  ${phone`
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 14px;
  `}
`;

const MetricIconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  ${phone`
    margin-bottom: 0;
    flex-shrink: 0;
    gap: 0;
  `}
`;

const MetricIcon = styled.span<{ color: string }>`
  font-size: 18px;
  padding: 8px;
  border-radius: 8px;
  background: ${({ color }) => color}15;
  color: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  ${phone`
    font-size: 20px;
    padding: 10px;
    border-radius: 10px;
    width: 40px;
    height: 40px;
  `}
`;

const MetricLabel = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  ${phone`
    font-size: 13px;
    letter-spacing: 0.03em;
  `}
`;

const MetricRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  ${phone`
    margin-left: auto;
    text-align: right;
  `}
`;

const MetricValue = styled.span`
  font-size: 40px;
  font-weight: 600;
  color: ${colors.text};
  ${phone`
    font-size: 28px;
    line-height: 1.2;
    white-space: nowrap;
  `}
`;

const MetricUnit = styled.span`
  font-size: 14px;
  color: ${colors.textMuted};
  ${phone`
    font-size: 13px;
  `}
`;

const MetricStatus = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.secondary};
  ${phone`
    display: none;
  `}
`;

// --- Tables ---
const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const Th = styled.th<{ nameCol?: boolean }>`
  padding: 12px 24px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.textMuted};
  width: ${({ nameCol }) => (nameCol ? '40%' : 'auto')};
  border-bottom: 1px solid #eeeeee;
  ${phone`
    padding: 10px 12px;
    font-size: 11px;
    letter-spacing: 0.03em;
  `}
`;

const Td = styled.td`
  padding: 14px 24px;
  font-size: 15px;
  color: ${colors.text};
  border-bottom: 1px solid #eeeeee;
  ${phone`
    padding: 12px 12px;
    font-size: 14px;
  `}
`;

const Tr = styled.tr`
  &:hover {
    background: #f0f7ff;
  }
`;

const Badge = styled.span<{ level: string }>`
  display: inline-block;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  background: ${({ level }) => {
    const l = level?.toUpperCase();
    if (l === 'HIGH') return '#e8f5e9';
    if (l === 'LOW') return '#ffebee';
    if (l === 'NORMAL') return '#e3f2fd';
    return '#f5f5f5';
  }};
  color: ${({ level }) => {
    const l = level?.toUpperCase();
    if (l === 'HIGH') return '#2e7d32';
    if (l === 'LOW') return '#c62828';
    if (l === 'NORMAL') return '#1565c0';
    return '#616161';
  }};
`;

const MutedCell = styled.span`
  color: ${colors.textMuted};
`;

// --- Back Button ---
const BackButton = styled.button`
  background: none;
  border: none;
  color: #495057;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-bottom: 16px;

  &:hover {
    background: #e8ecf0;
  }
`;

const Message = styled.div<{ isError?: boolean }>`
  padding: 16px;
  border-radius: 8px;
  background: ${({ isError }) => (isError ? '#ffebee' : '#e8f5e9')};
  color: ${({ isError }) => (isError ? '#c62828' : '#2e7d32')};
  font-size: 14px;
  text-align: center;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px;
`;

// === Helpers ===

const getWellnessLevel = (vs: any): 'HIGH' | 'NORMAL' | 'LOW' => {
  return vs?.wellnessLevel?.isEnabled && vs.wellnessLevel.value
    ? vs.wellnessLevel.value.toUpperCase()
    : 'NORMAL';
};

const getWellnessColor = (level: string) => {
  if (level === 'HIGH') return '#4caf50';
  if (level === 'LOW') return '#ef5350';
  return '#ff9800';
};

// === Component ===

const ResultTable = () => {
  const [vitalSigns] = useVitalSignsKey();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDateTime, setCurrentDateTime] = useState<string>('');
  const lang = useLang();
  const t = useT();

  const bloodPressureToDisplay =
    vitalSigns?.bloodPressure?.value?.systolic && vitalSigns?.bloodPressure?.value?.diastolic
      ? `${vitalSigns.bloodPressure.value.systolic}/${vitalSigns.bloodPressure.value.diastolic}`
      : '--';

  useEffect(() => {
    const validateData = () => {
      try {
        if (!vitalSigns || Object.keys(vitalSigns).length === 0) {
          throw new Error('No vital signs data available');
        }
        setError(null);
      } catch (err) {
        setError((err as Error).message || 'Failed to load results');
      } finally {
        setLoading(false);
      }
    };
    validateData();

    const updateDateTime = () => {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat(lang === 'tw' ? 'zh-HK' : 'en-HK', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Asia/Hong_Kong',
      });
      const parts = formatter.formatToParts(now);
      const year = parts.find((p) => p.type === 'year')?.value;
      const month = parts.find((p) => p.type === 'month')?.value;
      const day = parts.find((p) => p.type === 'day')?.value;
      const hour = parts.find((p) => p.type === 'hour')?.value;
      const minute = parts.find((p) => p.type === 'minute')?.value;
      setCurrentDateTime(`${year}-${month}-${day} ${hour}:${minute}`);
    };
    updateDateTime();
  }, [lang, vitalSigns]);

  const handleGoBack = () => {
    window.location.href = '/monitor';
  };

  const progressValue = vitalSigns?.wellnessIndex?.isEnabled && vitalSigns.wellnessIndex.value
    ? Math.min(Number(vitalSigns.wellnessIndex.value), 10)
    : 0;
  const wellnessLevel = getWellnessLevel(vitalSigns);
  const wellnessColor = getWellnessColor(wellnessLevel);

  const circumference = 2 * Math.PI * 45; // r=45

  if (loading) {
    return (
      <Wrapper>
        <SpinnerWrapper>
          <span>Loading...</span>
        </SpinnerWrapper>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Content>
          <Card>
            <CardBody>
              <Message isError>{error}</Message>
              <BackButton onClick={handleGoBack}>←</BackButton>
            </CardBody>
          </Card>
        </Content>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Content>
        <BackButton onClick={handleGoBack}>←</BackButton>

        {/* ===== 1. Hero Wellness Section ===== */}
        <Card>
          <CardBody>
            <WellnessCenter>
              <WellnessLabel><TableNameLink translationId="wellnessLevelValue" showIcon={false} /></WellnessLabel>
              <RingContainer>
                <RingSvg viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="#e8ecf0" strokeWidth="8" />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke={wellnessColor}
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - progressValue / 10)}
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </RingSvg>
                <RingCenter>
                  <RingScore level={wellnessLevel}>
                    {vitalSigns?.wellnessIndex?.isEnabled ? `${vitalSigns.wellnessIndex.value || '--'}/10` : 'N/A'}
                  </RingScore>
                  <RingBadge level={wellnessLevel}>
                    {vitalSigns?.wellnessLevel?.isEnabled ? vitalSigns.wellnessLevel.value || '--' : 'N/A'}
                  </RingBadge>
                </RingCenter>
              </RingContainer>

              <SegmentedBar>
                <SegmentsWrapper>
                  <SegmentLow />
                  <SegmentMid />
                  <SegmentHigh />
                </SegmentsWrapper>
                <SegmentLabels>
                  <span>0</span>
                  <span>Poor</span>
                  <span>Optimal</span>
                  <span>10</span>
                </SegmentLabels>
              </SegmentedBar>
            </WellnessCenter>
          </CardBody>
        </Card>

        {/* ===== 2. Key Metrics Bento ===== */}
        <BentoGrid>
          {/* Pulse Rate */}
          <MetricCard>
            <MetricIconRow>
              <MetricIcon color={colors.error}>♥</MetricIcon>
              <MetricLabel><TableNameLink translationId="pulseRateValue" showIcon={false} /></MetricLabel>
            </MetricIconRow>
            <MetricRow>
<MetricValue>
                {vitalSigns?.pulseRate?.isEnabled ? vitalSigns.pulseRate.value || '--' : 'N/A'}
              </MetricValue>
              <MetricUnit>BPM</MetricUnit>
            </MetricRow>
            <MetricStatus>✓ Stable</MetricStatus>
          </MetricCard>

          {/* Blood Pressure */}
          <MetricCard>
            <MetricIconRow>
              <MetricIcon color={colors.primary}>⏣</MetricIcon>
              <MetricLabel><TableNameLink translationId="bloodPressureValue" showIcon={false} /></MetricLabel>
            </MetricIconRow>
            <MetricRow>
                            <MetricValue>
                {vitalSigns?.bloodPressure?.isEnabled ? bloodPressureToDisplay : 'N/A'}
              </MetricValue>
              <MetricUnit>mmHg</MetricUnit>
            </MetricRow>
            <MetricStatus>✓ Measured</MetricStatus>
          </MetricCard>

          {/* Oxygen */}
          <MetricCard>
            <MetricIconRow>
              <MetricIcon color={colors.secondary}>◉</MetricIcon>
              <MetricLabel><TableNameLink translationId="oxygenSaturationValue" showIcon={false} /></MetricLabel>
            </MetricIconRow>
            <MetricRow>
                            <MetricValue>
                {vitalSigns?.oxygenSaturation?.isEnabled
                  ? `${vitalSigns.oxygenSaturation.value || '--'}`
                  : 'N/A'}
              </MetricValue>
              <MetricUnit>%</MetricUnit>
            </MetricRow>
            <MetricStatus>✓ Healthy</MetricStatus>
          </MetricCard>
        </BentoGrid>

        {/* ===== 3. Heart Health Table ===== */}
        <Card>
          <CardHeader>
<CardHeaderTitle><T id="tab_heart" /></CardHeaderTitle>
          </CardHeader>
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th nameCol><T id="table_name" /></Th>
                  <Th>數值/等級</Th>
                  <Th><T id="table_normal" /></Th>
                </tr>
              </thead>
              <tbody>
                <Tr>
                  <Td><TableNameLink translationId="respirationRateValue" /></Td>
                  <Td>{vitalSigns?.respirationRate?.isEnabled ? vitalSigns.respirationRate.value || '--' : 'N/A'} / —</Td>
                  <Td><MutedCell>12–20</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="hypertensionRiskValue" /></Td>
                  <Td>— / <Badge level={vitalSigns?.highBloodPressureRisk?.isEnabled ? vitalSigns.highBloodPressureRisk.value : 'NORMAL'}>
                    {vitalSigns?.highBloodPressureRisk?.isEnabled ? vitalSigns.highBloodPressureRisk.value || '--' : 'N/A'}
                  </Badge></Td>
                  <Td><MutedCell>LOW</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="highFastingGlucoseRiskValue" /></Td>
                  <Td>— / <Badge level={vitalSigns?.highFastingGlucoseRisk?.isEnabled ? vitalSigns.highFastingGlucoseRisk.value : 'NORMAL'}>
                    {vitalSigns?.highFastingGlucoseRisk?.isEnabled ? vitalSigns.highFastingGlucoseRisk.value || '--' : 'N/A'}
                  </Badge></Td>
                  <Td><MutedCell>LOW</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="highTotalCholesterolRiskValue" /></Td>
                  <Td>— / <Badge level={vitalSigns?.highTotalCholesterolRisk?.isEnabled ? vitalSigns.highTotalCholesterolRisk.value : 'NORMAL'}>
                    {vitalSigns?.highTotalCholesterolRisk?.isEnabled ? vitalSigns.highTotalCholesterolRisk.value || '--' : 'N/A'}
                  </Badge></Td>
                  <Td><MutedCell>LOW</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="highHbA1cRiskValue" /></Td>
                  <Td>— / <Badge level={vitalSigns?.highHbA1cRisk?.isEnabled ? vitalSigns.highHbA1cRisk.value : 'NORMAL'}>
                    {vitalSigns?.highHbA1cRisk?.isEnabled ? vitalSigns.highHbA1cRisk.value || '--' : 'N/A'}
                  </Badge></Td>
                  <Td><MutedCell>LOW</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="lowHemoglobinRiskValue" /></Td>
                  <Td>— / <Badge level={vitalSigns?.lowHemoglobinRisk?.isEnabled ? vitalSigns.lowHemoglobinRisk.value : 'NORMAL'}>
                    {vitalSigns?.lowHemoglobinRisk?.isEnabled ? vitalSigns.lowHemoglobinRisk.value || '--' : 'N/A'}
                  </Badge></Td>
                  <Td><MutedCell>LOW</MutedCell></Td>
                </Tr>
              </tbody>
            </Table>
          </div>
        </Card>

        {/* ===== 4. Energy & Stress Table ===== */}
        <Card>
          <CardHeader>
            <CardHeaderIcon>⚡</CardHeaderIcon>
            <CardHeaderTitle><T id="tab_stress" /></CardHeaderTitle>
          </CardHeader>
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th nameCol><T id="table_name" /></Th>
                  <Th><T id="table_index" /></Th>
                  <Th><T id="table_level" /></Th>
                  <Th><T id="table_normal" /></Th>
                </tr>
              </thead>
              <tbody>
                <Tr>
                  <Td><TableNameLink translationId="stressLevelValue" /></Td>
                  <Td>{vitalSigns?.stressIndex?.isEnabled ? vitalSigns.stressIndex.value || '--' : 'N/A'}</Td>
                  <Td>
                    <Badge level={vitalSigns?.stress?.isEnabled ? vitalSigns.stress.value : 'NORMAL'}>
                      {vitalSigns?.stress?.isEnabled ? vitalSigns.stress.value || '--' : 'N/A'}
                    </Badge>
                  </Td>
                  <Td><MutedCell>LOW/NORMAL</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="normalizedStressIndexValue" /></Td>
                  <Td>{vitalSigns?.normalizedStressIndex?.isEnabled ? vitalSigns.normalizedStressIndex.value || '--' : 'N/A'}</Td>
                  <Td>—</Td>
                  <Td><MutedCell>0–50</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="pnsZoneValue" /></Td>
                  <Td>{vitalSigns?.pnsIndex?.isEnabled ? vitalSigns.pnsIndex.value || '--' : 'N/A'}</Td>
                  <Td>
                    <Badge level={vitalSigns?.pnsZone?.isEnabled ? vitalSigns.pnsZone.value : 'NORMAL'}>
                      {vitalSigns?.pnsZone?.isEnabled ? vitalSigns.pnsZone.value || '--' : 'N/A'}
                    </Badge>
                  </Td>
                  <Td><MutedCell>NORMAL/HIGH</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="snsZoneValue" /></Td>
                  <Td>{vitalSigns?.snsIndex?.isEnabled ? vitalSigns.snsIndex.value || '--' : 'N/A'}</Td>
                  <Td>
                    <Badge level={vitalSigns?.snsZone?.isEnabled ? vitalSigns.snsZone.value : 'NORMAL'}>
                      {vitalSigns?.snsZone?.isEnabled ? vitalSigns.snsZone.value || '--' : 'N/A'}
                    </Badge>
                  </Td>
                  <Td><MutedCell>NORMAL</MutedCell></Td>
                </Tr>
              </tbody>
            </Table>
          </div>
        </Card>

        {/* ===== 5. Additional Vitals Table ===== */}
        <Card>
          <CardHeader>
            <CardHeaderIcon>📊</CardHeaderIcon>
            <CardHeaderTitle><T id="tab_vitals" /></CardHeaderTitle>
          </CardHeader>
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <Th nameCol><T id="table_name" /></Th>
                  <Th><T id="table_index" /></Th>
                  <Th><T id="table_normal" /></Th>
                </tr>
              </thead>
              <tbody>
                <Tr>
                  <Td><TableNameLink translationId="hemoglobinValue" /></Td>
                  <Td>
                    {vitalSigns?.hemoglobin?.isEnabled
                      ? `${vitalSigns.hemoglobin.value || '--'} g/dL`
                      : 'N/A'}
                  </Td>
                  <Td><MutedCell>12–18</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="hemoglobinA1CValue" /></Td>
                  <Td>
                    {vitalSigns?.hemoglobinA1c?.isEnabled
                      ? `${vitalSigns.hemoglobinA1c.value || '--'}%`
                      : 'N/A'}
                  </Td>
                  <Td><MutedCell>&lt; 5.7</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="meanRriValue" /></Td>
                  <Td>
                    {vitalSigns?.meanRri?.isEnabled
                      ? `${vitalSigns.meanRri.value || '--'} ms`
                      : 'N/A'}
                  </Td>
                  <Td><MutedCell>&lt; 1000</MutedCell></Td>
                </Tr>
                <Tr>
                  <Td><TableNameLink translationId="prqValue" /></Td>
                  <Td>{vitalSigns?.prq?.isEnabled ? vitalSigns.prq.value || '--' : 'N/A'}</Td>
                  <Td><MutedCell>~5</MutedCell></Td>
                </Tr>
              </tbody>
            </Table>
          </div>
        </Card>

        {/* Timestamp */}
        <div style={{ textAlign: 'center', color: '#6c757d', fontSize: '12px', paddingBottom: '24px' }}>
          {currentDateTime}
        </div>
      </Content>
    </Wrapper>
  );
};

export default ResultTable;
